import { useTranslation } from "react-i18next";
import { FormRow } from "./FormSectionComponent";
import { Grid, MenuItem, Typography } from "@mui/material";
import { useAppSelector } from "../../../../../../app/hooks";
import { Controller, useFormContext } from "react-hook-form";
import { FormKeys, FormSchedulingClassIds, WorkOfferType } from "../helpers";
import { SalaryType } from "../../../../types/salaryType";
import { SelectLight } from "../../../../../../common/components/styled/styled";
import SalaryComponents from "../../../../../talentProfile/editForms/salaries/SalaryComponents";
import {
  withFullSalaryMode,
  withInfoOnlyMode,
} from "../../../../../talentProfile/editForms/salaries/tesSalaryTableFormUtils";
import FormSection from "../../../../../../common/components/functional/formSection/FormSection";
import {
  WORK_AGREEMENT_ACCEPTED,
  WORK_AGREEMENT_DECLINED,
} from "../../../../constants";
import { WorkTimeType } from "../../../../types/workTimeType";
import { useCallback } from "react";

interface Props {
  type: WorkOfferType;
  schedulingClassIds: FormSchedulingClassIds;
  hasTargetedShiftSpecs: boolean;
}

export const Salary = ({
  type,
  schedulingClassIds,
  hasTargetedShiftSpecs,
}: Props) => {
  const { t } = useTranslation();
  const localized = useAppSelector((state) => state.localized);

  const { control, getValues, setValue, trigger, watch } = useFormContext();

  const {
    tesId,
    workTitleId,
    salaryComponentDefaultValues,
    components,
    tesSalaryTableSelection,
    workStarts,
    salaryType,
    workTimeType,
  } = watch();

  const status = getValues("status");
  const isReadOnly: boolean =
    status === WORK_AGREEMENT_ACCEPTED ||
    status === WORK_AGREEMENT_DECLINED ||
    type !== WorkOfferType.New;

  const isMonthly = salaryType === SalaryType.Monthly;
  const selectedSchedulingClassId = isMonthly
    ? schedulingClassIds.monthlySchedulingClassId
    : schedulingClassIds.hourlySchedulingClassId;
  const schedulingClass = localized.schedulingClass[selectedSchedulingClassId];

  const salaryTypeOptions =
    workTimeType !== WorkTimeType.Variable && !hasTargetedShiftSpecs
      ? [
          { value: SalaryType.Hourly, label: t("talentProfile:hourlyWage") },
          {
            value: SalaryType.Monthly,
            label: t("talentProfile:monthlySalary"),
          },
        ]
      : [{ value: SalaryType.Hourly, label: t("talentProfile:hourlyWage") }];

  const tesPickerDisabled = Object.keys(components || {}).length === 0;

  const handleAvailabilityChange = useCallback(
    (isAvailable: boolean) => {
      if (!isAvailable && getValues(FormKeys.TesSalaryTableSelection)) {
        setValue(FormKeys.TesSalaryTableSelection, null, {
          shouldDirty: true,
        });
      }
    },
    [getValues, setValue],
  );
  return (
    <>
      <FormSection title={t("common:salaryLong")}>
        <FormRow
          title={t("schedulingPeriods:schedulingPeriod")}
          component={<Typography>{schedulingClass}</Typography>}
        />
        <FormRow
          title={t("talentProfile:salaryTypeLabel")}
          component={
            <Controller
              control={control}
              name={FormKeys.SalaryType}
              render={({ field: { value, onChange } }) => (
                <SelectLight
                  name={FormKeys.SalaryType}
                  value={value}
                  onChange={onChange}
                  disabled={isReadOnly}
                  size="small"
                  sx={{ width: "155px" }}
                >
                  {salaryTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SelectLight>
              )}
            />
          }
        />
        <Grid container marginTop="1rem" />
        <SalaryComponents
          xsCols={[3, 8]}
          tesId={tesId}
          salaryType={salaryType}
          validFrom={workStarts}
          defaultValues={salaryComponentDefaultValues}
          edit={!isReadOnly}
          override={isReadOnly}
          tesSalaryTableProps={{
            disabled: isReadOnly || tesPickerDisabled,
            tesId,
            workTitleId,
            date: workStarts,
            value: tesSalaryTableSelection || null,
            onAvailabilityChange: handleAvailabilityChange,
            onChange: ({ selection }) => {
              setValue(
                FormKeys.TesSalaryTableSelection,
                withFullSalaryMode(selection),
                {
                  shouldDirty: true,
                },
              );
            },
          }}
          hasTesSalaryTableSelection={!!tesSalaryTableSelection}
          onManualBaseComponentChange={() => {
            const selection = getValues(FormKeys.TesSalaryTableSelection);
            if (!selection) return;
            setValue(
              FormKeys.TesSalaryTableSelection,
              withInfoOnlyMode(selection),
              {
                shouldDirty: true,
              },
            );
            trigger(FormKeys.Components);
          }}
        />
      </FormSection>
    </>
  );
};
